import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { InputStructure } from '../../structures/InputStructure';
import { DateSelectBlockProps } from '../types';
import { DatePickerItem } from './components/DatePickerItem';
import { ButtonClose } from '@/components/buttons';
import { formatDateTime } from '@/helpers/dateTime';
import { useComponentVisible } from '@/hooks/visibility';

import { ScProfileSelectBlock } from '../styled';

const formatDate = formatDateTime('dd.MM.yyyy');

type ProfileSelectDateBlockProps = DateSelectBlockProps & {
  onSelectDateRange: (dateRange: [Date | null, Date | null]) => void;
};

export const ProfileSelectDateBlock = forwardRef(
  (
    {
      error,
      hideErrorMessage,
      disabled,
      canReset,
      secondary,
      onSelectDateRange,
    }: ProfileSelectDateBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const { t } = useTranslation('bonuses');
    const { images } = useTheme();

    const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
      useComponentVisible(false);

    const handleOpen = () => {
      if (disabled) return;

      setIsComponentVisible(!isComponentVisible);
    };

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
      null,
      null,
    ]);
    const [startDate, endDate] = dateRange;

    const isDatesFull = useMemo(
      () => startDate !== null && endDate !== null,
      [startDate, endDate]
    );

    const placeholderStr = useMemo(
      () => `${t('datePicker.from')} / ${t('datePicker.to')}`,
      [t]
    );

    const handleClearInput = () => {
      setDateRange([null, null]);
      onSelectDateRange && onSelectDateRange([null, null]);
    };

    const handlerChange = useCallback(
      (dates: [Date | null, Date | null]) => {
        setDateRange(dates);

        onSelectDateRange && onSelectDateRange(dates);

        if (dates[0] && dates[1]) return setIsComponentVisible(false);
      },
      [onSelectDateRange, setIsComponentVisible]
    );

    const datePickerSettings = useMemo(
      () => ({
        startDate,
        endDate,
        onChange: handlerChange,
      }),
      [startDate, endDate, handlerChange]
    );

    const inputValue = useMemo(() => {
      const startValue = startDate ? `${formatDate(startDate)} / ` : '';
      const endValue = endDate
        ? `${formatDate(endDate)}`
        : startValue
          ? t('datePicker.to')
          : '';

      return startValue + endValue;
    }, [startDate, endDate, t]);

    const isCrossActive = (isDatesFull ? inputValue : undefined) && canReset;

    const selectedOption = inputValue
      ? {
          name: inputValue,
          value: `${startDate} / ${endDate}`,
        }
      : undefined;

    return (
      <InputStructure error={error} hideErrorMessage={hideErrorMessage}>
        <ScProfileSelectBlock
          ref={forwardedRef}
          active={isComponentVisible}
          error={!!error}
          disabled={!!disabled}
          className={isComponentVisible ? 'active' : ''}
          selected={isDatesFull}
          secondary={secondary}
        >
          <div ref={openerRef} className="select-button" onClick={handleOpen}>
            <div className="value">
              {selectedOption?.name}
              {!selectedOption && !!placeholderStr && (
                <span className="placeholder">{placeholderStr}</span>
              )}
            </div>
            <div className={` ${isCrossActive ? 'cross' : 'arrow'}`}>
              {isCrossActive ? (
                <ButtonClose onClick={handleClearInput} gray isStatic />
              ) : (
                <images.arrowUpGrayIcon.component />
              )}
            </div>
          </div>

          {isComponentVisible && (
            <div ref={ref}>
              <DatePickerItem {...datePickerSettings} secondary={secondary} />
            </div>
          )}
        </ScProfileSelectBlock>
      </InputStructure>
    );
  }
);
